import { MailingTypeEnum } from '@zaoblako/shared/modules/notification/enums/mailing.type.enum';
import { MailingTypeEnumI18n } from '@zaoblako/shared/modules/notification/i18n/mailing.type.enum.i18n';
import { MailingChannelEnum } from '@zaoblako/shared/modules/notification/enums/mailing.channel.enum';
import { MailingFileDebtorTargetFormDto, type MailingFileDebtorTargetFormInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-file-debtor-target-form.dto';
import { I18nLocalesEnum } from '@zaoblako/shared/common/enums/i18n.locales.enum';
import { MailingChannelEnumI18n } from '@zaoblako/shared/modules/notification/i18n/mailing.channel.enum.i18n';
import { EmailSenderFormDto, type EmailSenderFormInterface } from '@zaoblako/shared/modules/notification/dtos/mailing-settings/email-sender-form.dto';
import { SmsSenderFormDto, type SmsSenderFormInterface } from '@zaoblako/shared/modules/notification/dtos/mailing-settings/sms-sender-form.dto';
import { MailingAttachmentStatusEnum } from '@zaoblako/shared/modules/notification/enums/mailing-attachment-status.enum';
import { MailingAttachmentStatusEnumI18n } from '@zaoblako/shared/modules/notification/i18n/mailing-attachment-status-enum.i18n';
import { BillPaymentStatusEnum } from '@zaoblako/shared/modules/document/enums/bill.payment.status.enum';
import { BillPaymentStatusEnumI18n } from '@zaoblako/shared/modules/document/i18n/bill.payment.status.enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { NotificationDtoKeysEnum } from '@zaoblako/shared/modules/notification/enums/notification-dto-keys.enum';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { entityLikeSchema, entityLikeSchemaStrict } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';

import * as yup from 'yup';

/** Mailing and mailing template validation context data */
export interface MailingSerdeContextDataInterface extends SerdeContextDataInterface {
  /** The type of mailing or mailing template being created or updated */
  type: MailingTypeEnum;
}

/** Mailing and mailing template common request body - DTO signature */
export interface MailingBaseFormInterface {
  /** Mailing display name */
  name: string;
  /** The type of mailing or mailing template being created or updated */
  type: MailingTypeEnum;
  /** Mailing message title template (text with variables placeholders) */
  title: string;
  /** Mailing message text template (text with variables placeholders) */
  text: string;
  /**
   * Include advance payment for debtors. Using only when
   * {@link MailingBaseFormInterface.type} property equals
   * {@link MailingTypeEnum.DEBTORS}.
   */
  includingAdvancePayment?: boolean;
  /**
   * Attach bills when mailing running. Using only when
   * {@link MailingBaseFormInterface.type} property equals
   * {@link MailingTypeEnum.DEBTORS} or {@link MailingTypeEnum.DEBTORS_FROM_FILE}.
   */
  attachBills?: boolean;
  /** Mailing messaging channel */
  channel: MailingChannelEnum;
  /** Email sender settings */
  emailSender?: EmailSenderFormInterface;
  /** SMS sender settings */
  smsSender?: SmsSenderFormInterface;
  /**
   * Companies whose debts to which counterparties must be notified.
   * Using only when {@link MailingBaseFormInterface.type} property
   * equals {@link MailingTypeEnum.DEBTORS} or {@link MailingTypeEnum.BILLS}.
   */
  companies?: CompanyFindInterface[];
  /**
   * Counteraprties which must be notified.
   * Using only when {@link MailingBaseFormInterface.type} property equals {@link MailingTypeEnum.DEBTORS}.
   */
  contragents?: ContragentFindInterface[];
  /** Recipients of {@link MailingTypeEnum.DEBTORS_FROM_FILE} mailing */
  fileDebtorTargets?: MailingFileDebtorTargetFormInterface[];
  /** Recipient contact persons of {@link MailingTypeEnum.HELPDESK} mailing template */
  externalUsers: ExternalUserFindInterface[];
  /** Recipient users of {@link MailingTypeEnum.HELPDESK} mailing template */
  recipientUsers?: UserFindInterface[];
  /**
   * Take only attachments (documents, bills etc.) with these statuses.
   * Using only when {@link MailingBaseFormInterface.type} property equals {@link MailingTypeEnum.BILLS}.
   */
  attachmentStatuses?: MailingAttachmentStatusEnum[];
  /**
   * Directions to filter bills.
   * Using only when {@link MailingBaseFormInterface.type} property equals {@link MailingTypeEnum.BILLS}.
   */
  billingDirections?: DirectionFindInterface[];
  /**
   * Payment statuses to filter bills.
   * Using only when {@link MailingBaseFormInterface.type} property equals {@link MailingTypeEnum.BILLS}.
   */
  billingPaymentStatuses?: BillPaymentStatusEnum[];
  /** Users who will receive notifications about the execution of the mailing */
  userAuditors?: UserFindInterface[];
  /** Groups of users who will receive notifications about the execution of the mailing */
  groupAuditors?: UserGroupFindInterface[];
}

/**
 * Create and return function which returns `false` with mailing type in context or form value is not in received types list
 *
 * @param types Types list to check (variadic)
 *
 * @returns Funciton which can be applied to `optional` or `hidden` DTO field setting
 */
export const falseWhenTypeNotIn = (...types: MailingTypeEnum[]) => (
  context: SerdeContextInterface<MailingSerdeContextDataInterface, MailingBaseFormInterface>,
): boolean => !types.includes(context?.data?.type || context?.value?.type)

/**
 * Create and return function which returns `false` with mailing type in context or form value is not in received types list
 *
 * @param types Types list to check (variadic)
 *
 * @returns Funciton which can be applied to `optional` or `hidden` DTO field setting
 */
export const trueWhenTypeNotIn = (...types: MailingTypeEnum[]) => (
  context: SerdeContextInterface<MailingSerdeContextDataInterface, MailingBaseFormInterface>,
): boolean => types.includes(context?.data?.type || context?.value?.type)

/**
 * Create and return function which returns additional hint for DTO field which describes
 * which type of mailing or mailing template requires this value.
 *
 * @param types Types list to check (variadic)
 *
 * @returns Funciton which can be applied to `hint` DTO field setting
 */
export const requiredOnTypesHint = (...types: MailingTypeEnum[]) => (
  context: SerdeContextInterface<MailingSerdeContextDataInterface, MailingBaseFormInterface>,
): string => {
  if (!types?.length) {
    return undefined;
  }

  const locale = context?.data?.locale || I18nLocalesEnum.RU_RU;
  return `Значение обязательно для следующих типов рассылок и шаблонов рассылок:
${
  types
    .map(type => `* "${MailingTypeEnumI18n[locale][type]}" (поле \`type\` равно \`'${type}'\`);`)
    .join('\n')
}
`;
};

/** Mailing and mailing template common request body - DTO schema */
export const MailingBaseFormDto = SerdeDtoClass.create<MailingBaseFormInterface>(NotificationDtoKeysEnum.MAILING_BASE_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: nonEmptyStringSchema,
  },
  type: {
    label: 'Тип рассылки',
    serializer: commonEnumValidator(MailingTypeEnum, MailingTypeEnumI18n),
  },
  title: {
    label: 'Тема письма',
    serializer: nonEmptyStringSchema,
  },
  text: {
    label: 'Текст сообщения',
    serializer: nonEmptyStringSchema,
  },
  includingAdvancePayment: {
    optional: true,
    hidden: trueWhenTypeNotIn(MailingTypeEnum.DEBTORS, MailingTypeEnum.DEBTORS_FROM_FILE),
    label: 'С учетом аванс',
    serializer: yup.boolean(),
  },
  attachBills: {
    optional: true,
    hidden: trueWhenTypeNotIn(MailingTypeEnum.DEBTORS),
    label: 'Отправлять счета',
    serializer: yup.boolean(),
  },
  channel: {
    label: 'Канал',
    serializer: commonEnumValidator(MailingChannelEnum, MailingChannelEnumI18n),
  },
  emailSender: {
    optional: context => (<MailingBaseFormInterface>context.value)?.channel !== MailingChannelEnum.EMAIL,
    label: 'Настройки почтовой рассылки',
    nested: EmailSenderFormDto,
    serializer: (context, nested): yup.Schema<any> => (<MailingBaseFormInterface>context.value)?.channel === MailingChannelEnum.EMAIL
      ? nested
      : yup
        .mixed()
        .notRequired(),
  },
  smsSender: {
    optional: context => (<MailingBaseFormInterface>context.value)?.channel !== MailingChannelEnum.SMS,
    label: 'Настройки почтовой рассылки',
    nested: SmsSenderFormDto,
    serializer: (context, nested): yup.Schema<any> => (<MailingBaseFormInterface>context.value)?.channel === MailingChannelEnum.SMS
      ? nested
      : yup
        .mixed()
        .notRequired(),
  },
  companies: {
    optional: falseWhenTypeNotIn(MailingTypeEnum.DEBTORS),
    hidden: trueWhenTypeNotIn(MailingTypeEnum.DEBTORS, MailingTypeEnum.BILLS, MailingTypeEnum.DOCUMENTS),
    label: 'Компании',
    hint: requiredOnTypesHint(MailingTypeEnum.DEBTORS),
    serializer: (context: SerdeContextInterface<MailingSerdeContextDataInterface, MailingBaseFormInterface>) => {
      const isDebtors = (context?.data?.type || context?.value?.type) === MailingTypeEnum.DEBTORS;
      return isDebtors
        ? yup
          .array(entityLikeSchemaStrict)
          .min(1, DtoMessageEnum.EMPTY_ARRAY)
        : yup.array(entityLikeSchema)
    },
    checkers: [ClientCheckerKeysEnum.COMPANY_ASSERT_EXISTS_BY_DTO],
  },
  contragents: {
    optional: true,
    hidden: trueWhenTypeNotIn(MailingTypeEnum.DEBTORS, MailingTypeEnum.BILLS, MailingTypeEnum.DOCUMENTS),
    label: 'Контрагенты',
    serializer: yup.array(entityLikeSchema),
    checkers: [ClientCheckerKeysEnum.CONTRAGENT_ASSERT_EXISTS_BY_DTO],
  },
  fileDebtorTargets: {
    optional: falseWhenTypeNotIn(MailingTypeEnum.DEBTORS_FROM_FILE),
    hidden: trueWhenTypeNotIn(MailingTypeEnum.DEBTORS_FROM_FILE),
    label: 'Должники из файла',
    nested: MailingFileDebtorTargetFormDto,
    serializer: (
      context: SerdeContextInterface<MailingSerdeContextDataInterface, MailingBaseFormInterface>,
      nested,
    ) => {
      const isDebtorsFromFile = (context?.data?.type || context?.value?.type) === MailingTypeEnum.DEBTORS_FROM_FILE;
      return isDebtorsFromFile
        ? yup
          .array(nested)
          .min(1, DtoMessageEnum.EMPTY_ARRAY)
        : yup.array(nested);
    },
  },
  externalUsers: {
    optional: falseWhenTypeNotIn(MailingTypeEnum.HELPDESK),
    hidden: trueWhenTypeNotIn(MailingTypeEnum.HELPDESK),
    label: 'Внешние пользователи',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.EXTERNAL_USER_ASSERT_EXISTS_BY_DTO],
  },
  recipientUsers: {
    optional: true,
    hidden: trueWhenTypeNotIn(MailingTypeEnum.HELPDESK),
    label: 'Пользователи-получатели',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  attachmentStatuses: {
    optional: true,
    hidden: trueWhenTypeNotIn(MailingTypeEnum.BILLS, MailingTypeEnum.DOCUMENTS),
    label: 'Состояние',
    serializer: commonEnumValidator.forArray(MailingAttachmentStatusEnum, MailingAttachmentStatusEnumI18n),
  },
  billingDirections: {
    optional: true,
    hidden: trueWhenTypeNotIn(MailingTypeEnum.BILLS),
    label: 'БС',
    serializer: yup.array(entityLikeSchema),
    checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
  },
  billingPaymentStatuses: {
    optional: true,
    hidden: trueWhenTypeNotIn(MailingTypeEnum.BILLS),
    label: 'Статусы счетов',
    serializer: commonEnumValidator.forArray(BillPaymentStatusEnum, BillPaymentStatusEnumI18n),
  },
  userAuditors: {
    optional: true,
    label: 'Пользователи-аудиторы',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  groupAuditors: {
    optional: true,
    label: 'Группы-аудиторы',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
  },
});
